/* ═══════════════════════════════════════════════════════════════
   The Designer — the forma's data life in Design mode.

   Palette edits land on a local DRAFT immediately (the paper restyles
   on the same frame), then write through to the EXISTING forma model
   silently on a short debounce:

     · a PROJECT forma copy saves straight onto its own row;
     · a MASTER resolved for a project spec is copy-on-write — the
       first save forks the project's copy via the same
       FormaService.ensureProjectCopy the rest of the app uses, and
       every subsequent edit lands on that copy. The master is never
       touched from inside a project. (Josh law: masters are templates.)

   Undo/redo here is the forma-edit history (snapshots, like the
   classic forma view). The document editor keeps its OWN history —
   the two are merged into one user-facing timeline by the undo
   coordinator (undoCoordinator.ts, contract §2.7), which is why
   `edit` reports every user action through `onAction` and undo/redo
   report whether they actually moved.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { FormaService } from '../services/formaService';
import { pushEditHistory } from '../../shared/editHistory';
import { reportError } from '../services/errorLog';

const SAVE_DEBOUNCE_MS = 1200;
const UNDO_LIMIT = 100;

export interface FormaDraftProject {
  id: string;
  number?: string;
  name?: string;
}

export interface FormaDraft {
  /** The forma the Design-mode paper renders and the palettes edit. */
  forma: StandardConfig | null;
  /** Unsaved palette edits exist (a write is scheduled). */
  dirty: boolean;
  saving: boolean;
  /** True once the draft is (or has been forked into) a project copy. */
  isProjectCopy: boolean;
  /** Apply an edit. Pure transform — never mutate the input. */
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  /** Write now (mode switch, leaving the surface). */
  flush: () => Promise<void>;
  /** Undo one formatting step. False = there was nothing to undo. */
  undo: () => boolean;
  /** Redo one formatting step. False = there was nothing to redo. */
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
}

export function useFormaDraft(
  base: StandardConfig | null,
  project: FormaDraftProject | null,
  userName: string,
  userId: string | undefined,
  onError: (msg: string) => void,
  /** Called once per USER edit (never for undo/redo) so the workspace's
   *  universal-undo timeline knows a formatting action just happened. */
  onAction?: () => void
): FormaDraft {
  const [forma, setForma] = React.useState<StandardConfig | null>(base);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [historyTick, setHistoryTick] = React.useState(0);

  const draftRef = React.useRef<StandardConfig | null>(base);
  const dirtyRef = React.useRef(false);
  const savingRef = React.useRef(false);
  const queuedRef = React.useRef(false);
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const undoRef = React.useRef<StandardConfig[]>([]);
  const redoRef = React.useRef<StandardConfig[]>([]);
  const projectRef = React.useRef(project);
  projectRef.current = project;
  const userRef = React.useRef({ userName, userId });
  userRef.current = { userName, userId };
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;
  const onActionRef = React.useRef(onAction);
  onActionRef.current = onAction;

  // A different forma arriving from the resolver replaces a CLEAN draft.
  // A dirty draft is the user's newer truth — never stomp it with a re-read.
  React.useEffect(() => {
    if (dirtyRef.current || savingRef.current) return;
    if (base?.id !== draftRef.current?.id || base !== draftRef.current) {
      draftRef.current = base;
      setForma(base);
      undoRef.current = [];
      redoRef.current = [];
      setHistoryTick((t) => t + 1);
    }
  }, [base]);

  const writeNow = React.useCallback(async () => {
    const draft = draftRef.current;
    if (!draft || !dirtyRef.current) return;
    if (savingRef.current) {
      queuedRef.current = true;
      return;
    }
    savingRef.current = true;
    setSaving(true);
    dirtyRef.current = false;
    setDirty(false);
    try {
      const { userName: name, userId: uid } = userRef.current;
      let toSave: StandardConfig = draft;
      const proj = projectRef.current;
      if (!draft.projectId && proj) {
        // Copy-on-write: fork the project's copy of this master, then land
        // the user's edits on the copy. Existing model, silent.
        const copy = await FormaService.ensureProjectCopy(draft, proj);
        toSave = {
          ...draft,
          id: copy.id,
          projectId: copy.projectId,
          sourceFormaId: copy.sourceFormaId,
          specId: undefined,
          attachedProjectId: undefined,
          editHistory: copy.editHistory,
          docSettings: {
            ...draft.docSettings,
            standardName: copy.docSettings?.standardName || draft.docSettings.standardName,
          },
          bindings:
            draft.bindings && Object.keys(draft.bindings).length
              ? draft.bindings
              : copy.bindings || {},
        };
      }
      toSave = {
        ...toSave,
        editHistory: pushEditHistory(toSave.editHistory, name, uid, 'edited'),
      };
      await FormaService.save(toSave);
      // The draft continues from the SAVED identity (a forked copy keeps
      // receiving edits under its own id), but any keystrokes that landed
      // mid-save stay: re-point only the identity fields.
      const cur = draftRef.current;
      if (cur) {
        const next: StandardConfig = {
          ...cur,
          id: toSave.id,
          projectId: toSave.projectId,
          sourceFormaId: toSave.sourceFormaId,
          specId: toSave.specId,
          editHistory: toSave.editHistory,
          docSettings: {
            ...cur.docSettings,
            standardName: toSave.docSettings.standardName,
          },
        };
        draftRef.current = next;
        setForma(next);
      }
    } catch (err) {
      reportError('Designer:formaSave', err);
      dirtyRef.current = true;
      setDirty(true);
      onErrorRef.current('Formatting change not saved yet — retrying');
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (queuedRef.current || dirtyRef.current) {
        queuedRef.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => void writeNow(), SAVE_DEBOUNCE_MS);
      }
    }
  }, []);

  const schedule = React.useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void writeNow(), SAVE_DEBOUNCE_MS);
  }, [writeNow]);

  const edit = React.useCallback(
    (fn: (f: StandardConfig) => StandardConfig) => {
      const cur = draftRef.current;
      if (!cur) return;
      undoRef.current.push(cur);
      if (undoRef.current.length > UNDO_LIMIT) undoRef.current.shift();
      redoRef.current = [];
      const next = fn(cur);
      draftRef.current = next;
      setForma(next);
      dirtyRef.current = true;
      setDirty(true);
      setHistoryTick((t) => t + 1);
      schedule();
      onActionRef.current?.();
    },
    [schedule]
  );

  const undo = React.useCallback((): boolean => {
    const prev = undoRef.current.pop();
    if (!prev || !draftRef.current) return false;
    redoRef.current.push(draftRef.current);
    draftRef.current = prev;
    setForma(prev);
    dirtyRef.current = true;
    setDirty(true);
    setHistoryTick((t) => t + 1);
    schedule();
    return true;
  }, [schedule]);

  const redo = React.useCallback((): boolean => {
    const next = redoRef.current.pop();
    if (!next || !draftRef.current) return false;
    undoRef.current.push(draftRef.current);
    draftRef.current = next;
    setForma(next);
    dirtyRef.current = true;
    setDirty(true);
    setHistoryTick((t) => t + 1);
    schedule();
    return true;
  }, [schedule]);

  const flush = React.useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await writeNow();
  }, [writeNow]);

  // Leaving mid-edit must not lose the edit.
  React.useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (dirtyRef.current) void writeNow();
    },
    [writeNow]
  );

  void historyTick; // state exists to re-render canUndo/canRedo

  return {
    forma,
    dirty,
    saving,
    isProjectCopy: !!forma?.projectId,
    edit,
    flush,
    undo,
    redo,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
  };
}
